import {
  Avatar,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Stack,
  Theme,
  Typography,
} from '@mui/material'
import { BaseSyntheticEvent, forwardRef } from 'react'
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form'
import {
  IconButton,
  Icons,
  ScrollTag,
  Switch,
  TextInputField,
  TextInputFieldProps,
} from 'utoniq-ui'

import {
  ArtistThreadCategory,
  CreateArtistThreadInput,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { useImageEditor } from '@/components/organisms/imageEditor'
import { tLabel, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, UseFormProps } from '@/core/form'

export type ThreadCreateFormProps = {
  hasPatron: boolean
  categoryOptions: {
    label: string
    value: Maybe<ArtistThreadCategory> | undefined
  }[]
  register: UseFormRegister<ThreadFormValues>
  errors: FieldErrors<ThreadFormValues>
  control: Control<ThreadFormValues>
  setValue: UseFormSetValue<ThreadFormValues>
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>
  userId: string
}

export type ThreadFormValues = Omit<
  CreateArtistThreadInput,
  'artistId' | 'status' | 'thumbnail'
> & { thumbnail?: { tmpPath?: string; fileUrl?: string } }

export const schema: UseFormProps['schema'] = o => ({
  thumbnail: o
    .object({
      tmpPath: o.string().allow('').optional(),
      fileUrl: o.string().allow('').optional(),
    })
    .allow('')
    .optional(),
  name: o.string().label(tLabel('threadCreate.name')),
  description: o.string().label(tLabel('threadCreate.description')),
  category: o
    .string()
    .label(tLabel('threadCreate.category'))
    .allow('')
    .optional(),
  isPremium: o
    .boolean()
    .label(tLabel('threadCreate.isPremium'))
    .allow(false)
    .optional(),
})

export const defaultValues: ThreadFormValues = {
  name: '',
  description: '',
  thumbnail: {
    tmpPath: '',
    fileUrl: '',
  },
  isPremium: false,
  category: undefined,
}

export const ThreadCreateForm: Organism<ThreadCreateFormProps> = ({
  hasPatron,
  onSubmit,
  register,
  errors,
  control,
  setValue,
  categoryOptions,
  userId,
}) => {
  return (
    <Form onSubmit={onSubmit}>
      <ImageCell setValue={setValue} userId={userId} />
      <Box sx={{ px: 5 }}>
        <TextInputCell
          type="text"
          error={errors?.name}
          {...register('name')}
          textFieldProps={{
            minRows: 1,
            maxRows: 5,
            inputProps: {
              sx: ({ typography }: Theme) => typography.h4,
            },
            variant: 'standard',
            InputProps: {
              sx: { border: 'none', px: 0, pt: 5, pb: 0 },
              disableUnderline: true,
            },
          }}
        />
        <TextInputCell
          type="text"
          error={errors?.description}
          {...register('description')}
          textFieldProps={{
            minRows: 3,
            maxRows: 10,
            inputProps: {
              sx: ({ typography }: Theme) => typography.body1,
            },
            variant: 'standard',
            InputProps: {
              sx: { border: 'none', px: 0, pt: 5, pb: 0 },
              disableUnderline: true,
            },
          }}
        />
        <HeadingTextCell />
        <ThreadTypeCell
          control={control}
          setValue={setValue}
          categoryOptions={categoryOptions}
        />
        {hasPatron && <MemberSetCell control={control} setValue={setValue} />}
      </Box>
    </Form>
  )
}

type ImageCellProps = {
  setValue: UseFormSetValue<ThreadFormValues>
  userId: string
}
const ImageCell: Cell<ImageCellProps> = ({ setValue, userId }) => {
  const { t } = useTranslation('threadCreate')
  const {
    ImageEditModal,
    ModalProps,
    handleChoseFile,
    handleRemoveFile,
    filePathAfterCrop,
  } = useImageEditor({
    userId,
    callback: async data => setValue('thumbnail', data, { shouldDirty: true }),
  })
  return (
    <CellFrame>
      {filePathAfterCrop ? (
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={filePathAfterCrop}
            variant="square"
            sx={{ width: '100%', height: 281 }}
          />
          <IconButton.Close
            onClick={handleRemoveFile}
            size="small"
            bgcolor="grey.50"
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
          />
        </Box>
      ) : (
        <Box sx={{ px: 5 }}>
          <Button variant="text" sx={{ ml: -2 }} onClick={handleChoseFile}>
            <Icons.Image />
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ ml: 2 }}
            >
              {t('attachImage')}
            </Typography>
          </Button>
        </Box>
      )}
      <ImageEditModal {...ModalProps} />
    </CellFrame>
  )
}

const TextInputCell = forwardRef(
  ({ textFieldProps, ...props }: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('threadCreate')
    return (
      <CellFrame>
        <TextInputField
          {...props}
          ref={ref}
          t={t}
          textFieldProps={{
            sx: { p: 0, m: 0, bgcolor: 'transparent', width: '100%' },
            multiline: true,
            helperText: '',
            ...textFieldProps,
          }}
        />
      </CellFrame>
    )
  }
)

const HeadingTextCell: Cell = () => {
  const { t } = useTranslation('threadCreate')
  return (
    <CellFrame sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle1">{t('selectCategory')}</Typography>
        <Typography variant="overline">{t('any')}</Typography>
      </Stack>
    </CellFrame>
  )
}

type ThreadTypeCellProps = {
  control: Control<ThreadFormValues>
  setValue: UseFormSetValue<ThreadFormValues>
  categoryOptions: ThreadCreateFormProps['categoryOptions']
}
const ThreadTypeCell: Cell<ThreadTypeCellProps> = ({
  control,
  setValue,
  categoryOptions,
}) => {
  const category = useWatch({ control, name: 'category' })
  return (
    <CellFrame>
      <ScrollTag
        value={category}
        tags={categoryOptions}
        onClick={value =>
          setValue('category', category === value ? undefined : value)
        }
      />
    </CellFrame>
  )
}

type MemberSetCellProps = {
  control: Control<ThreadFormValues>
  setValue: UseFormSetValue<ThreadFormValues>
}
const MemberSetCell: Cell<MemberSetCellProps> = ({ control, setValue }) => {
  const { t } = useTranslation('threadCreate')
  const isPremium = useWatch({ control, name: 'isPremium' })
  return (
    <CellFrame sx={{ pt: 5 }}>
      <List>
        <ListItem
          secondaryAction={
            <Switch
              checked={isPremium}
              onChange={(event, checked) => {
                setValue('isPremium', checked)
              }}
            />
          }
          sx={{ p: 0, '.MuiListItemSecondaryAction-root': { right: 0 } }}
        >
          <ListItemText
            primary={t('membersOnly')}
            slotProps={{ primary: { variant: 'subtitle1' } }}
          />
        </ListItem>
      </List>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ pt: 3, pb: 5 }}
      >
        <Icons.Help fontSize="xSmall" />
        <Typography variant="caption" color="text.secondary">
          {t('membersOnlyNote')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}
